import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuth'
import {
  FileText, Clock, Calendar, Users, Lock, Play, CheckCircle,
  AlertTriangle, Filter, Search, Plus, Shield
} from 'lucide-react'
import toast from 'react-hot-toast'

interface Exam {
  id: string
  title: string
  subject: string
  duration: number
  totalQuestions: number
  maxScore: number
  startTime: string
  endTime: string
  status: 'upcoming' | 'active' | 'completed'
  attempts: number
  maxAttempts: number
  securityLevel: 'standard' | 'high' | 'maximum'
  aiProctored: boolean
  randomQuestions: boolean
  antiCheatEnabled: boolean
}

export default function Exams() {
  const { user } = useAuthStore()
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [showCreateModal, setShowCreateModal] = useState(false)

  const exams: Exam[] = [
    {
      id: '1', title: 'Mathematics Final Exam', subject: 'Advanced Math',
      duration: 120, totalQuestions: 50, maxScore: 100,
      startTime: '2026-05-10T10:00:00', endTime: '2026-05-10T12:00:00',
      status: 'upcoming', attempts: 0, maxAttempts: 1,
      securityLevel: 'maximum', aiProctored: true, randomQuestions: true, antiCheatEnabled: true
    },
    {
      id: '2', title: 'Python Programming Test', subject: 'Programming',
      duration: 90, totalQuestions: 30, maxScore: 100,
      startTime: '2026-05-05T14:00:00', endTime: '2026-05-05T15:30:00',
      status: 'active', attempts: 0, maxAttempts: 2,
      securityLevel: 'high', aiProctored: true, randomQuestions: true, antiCheatEnabled: true
    },
    {
      id: '3', title: 'Physics Midterm', subject: 'Physics',
      duration: 60, totalQuestions: 25, maxScore: 100,
      startTime: '2026-04-28T09:00:00', endTime: '2026-04-28T10:00:00',
      status: 'completed', attempts: 1, maxAttempts: 1,
      securityLevel: 'standard', aiProctored: false, randomQuestions: true, antiCheatEnabled: false
    },
    {
      id: '4', title: 'English Literature Quiz', subject: 'Language',
      duration: 30, totalQuestions: 15, maxScore: 50,
      startTime: '2026-05-06T11:00:00', endTime: '2026-05-06T11:30:00',
      status: 'upcoming', attempts: 0, maxAttempts: 3,
      securityLevel: 'standard', aiProctored: false, randomQuestions: false, antiCheatEnabled: false
    },
  ]

  const filtered = exams.filter(e => {
    const matchesFilter = filter === 'all' || e.status === filter
    const matchesSearch = e.title.toLowerCase().includes(search.toLowerCase()) ||
                         e.subject.toLowerCase().includes(search.toLowerCase())
    return matchesFilter && matchesSearch
  })

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'active': return 'bg-success/20 text-success'
      case 'upcoming': return 'bg-warning/20 text-warning'
      case 'completed': return 'bg-slate-700 text-slate-400'
      default: return 'bg-slate-700 text-slate-400'
    }
  }

  const getSecurityBadge = (exam: Exam) => {
    if (exam.antiCheatEnabled && exam.aiProctored) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-danger/20 text-danger rounded text-xs font-medium">
          <Shield className="w-3 h-3" /> MAXIMUM SECURITY
        </span>
      )
    } else if (exam.antiCheatEnabled) {
      return (
        <span className="flex items-center gap-1 px-2 py-1 bg-warning/20 text-warning rounded text-xs font-medium">
          <Shield className="w-3 h-3" /> HIGH SECURITY
        </span>
      )
    }
    return null
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Exams & Assessments</h1>
          <p className="text-slate-500 mt-1">Manage your exams with AI-powered anti-cheating</p>
        </div>
        {user?.role === 'admin' && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Create Exam
          </button>
        )}
      </div>

      {/* Security Notice */}
      <div className="glass rounded-xl p-4 border border-primary/30 bg-primary/5">
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-primary mt-0.5" />
          <div>
            <h3 className="font-medium text-sm">Anti-Cheating System Active</h3>
            <p className="text-xs text-slate-500 mt-1">
              AI proctoring, random question generation, tab switching detection, and copy-paste blocking are enabled for secure exams.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search exams..."
            className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'upcoming', 'completed'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === f ? 'bg-primary text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Exams Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filtered.map(exam => (
          <div key={exam.id} className="glass rounded-xl p-5 hover:border-primary/30 transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <FileText className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{exam.title}</h3>
                  <p className="text-xs text-slate-500">{exam.subject}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(exam.status)}`}>
                {exam.status.toUpperCase()}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                <p className="text-lg font-bold">{exam.duration}</p>
                <p className="text-xs text-slate-500">Minutes</p>
              </div>
              <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                <p className="text-lg font-bold">{exam.totalQuestions}</p>
                <p className="text-xs text-slate-500">Questions</p>
              </div>
              <div className="text-center p-2 bg-slate-800/50 rounded-lg">
                <p className="text-lg font-bold">{exam.maxScore}</p>
                <p className="text-xs text-slate-500">Max Score</p>
              </div>
            </div>

            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Calendar className="w-3 h-3" />
                {new Date(exam.startTime).toLocaleDateString()}
                <Clock className="w-3 h-3 ml-2" />
                {new Date(exam.startTime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </div>
              {getSecurityBadge(exam)}
            </div>

            {exam.status === 'active' && exam.attempts < exam.maxAttempts && (
              <Link
                to={`/exams/${exam.id}/take`}
                className="w-full flex items-center justify-center gap-2 py-2.5 bg-primary hover:bg-primary-dark rounded-lg font-medium transition-colors"
              >
                <Play className="w-4 h-4" />
                Start Exam (Anti-Cheat Active)
              </Link>
            )}
            {exam.status === 'completed' && (
              <div className="flex items-center justify-center gap-2 py-2.5 bg-slate-800 rounded-lg text-slate-500">
                <CheckCircle className="w-4 h-4" />
                Completed
              </div>
            )}
            {exam.status === 'upcoming' && (
              <div className="flex items-center justify-center gap-2 py-2.5 bg-slate-800 rounded-lg text-slate-500">
                <Lock className="w-4 h-4" />
                Locked until start time
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
